export const DEFAULT_CUSTOMER_TYPE_OPTIONS = [
  { value: "general", label: "General" },
  { value: "service", label: "Service" },
  { value: "banker", label: "Banker" },
]

export const normalizeCustomerTypeValue = value =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")

export const formatCustomerTypeLabel = value => {
  const normalized = normalizeCustomerTypeValue(value)
  if (!normalized) return ""

  return normalized
    .split("_")
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ")
}

export const buildSuggestionOptions = values =>
  (values || [])
    .map(item => {
      if (typeof item === "string") {
        return { value: item, label: item }
      }

      if (item?.value !== undefined && item?.label !== undefined) {
        return { value: item.value, label: item.label }
      }

      if (item?.name) {
        return { value: item.name, label: item.name }
      }

      return null
    })
    .filter(Boolean)

export const buildCreatableOptions = buildSuggestionOptions

export const buildCustomerTypeOptions = customerTypes => {
  const options = buildSuggestionOptions(customerTypes)
  const existingKeys = new Set(options.map(option => normalizeCustomerTypeValue(option.value)))

  DEFAULT_CUSTOMER_TYPE_OPTIONS.forEach(option => {
    if (!existingKeys.has(normalizeCustomerTypeValue(option.value))) {
      options.push(option)
    }
  })

  return options
}

export const resolveCustomerTypeKey = value => {
  const normalized = normalizeCustomerTypeValue(value)

  return ["general", "service", "banker"].includes(normalized) ? normalized : ""
}

export const getCustomerTypeFieldMeta = customerTypeKey => {
  if (customerTypeKey === "banker") {
    return {
      requiresDesignation: true,
      organizationField: "bank_name",
      organizationLabel: "Bank Name",
      branchField: "branch_name",
      branchLabel: "Branch Name",
      showBranch: true,
    }
  }

  if (customerTypeKey === "service") {
    return {
      requiresDesignation: true,
      organizationField: "company_name",
      organizationLabel: "Company Name",
      branchField: "branch_name",
      branchLabel: "Branch Name",
      showBranch: false,
    }
  }

  return {
    requiresDesignation: false,
    organizationField: "company_name",
    organizationLabel: "Company Name",
    branchField: "branch_name",
    branchLabel: "Branch Name",
    showBranch: false,
  }
}

export const buildCustomerSuggestionBuckets = customers => {
  const distinctValues = selector => {
    const seen = new Set()

    return (customers || [])
      .map(selector)
      .map(value => String(value || "").trim())
      .filter(Boolean)
      .filter(value => {
        const key = value.toLowerCase()
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
      .sort((left, right) => left.localeCompare(right))
  }

  return {
    designations: buildSuggestionOptions(distinctValues(customer => customer?.designation)),
    companies: buildSuggestionOptions(distinctValues(customer => customer?.company_name)),
    banks: buildSuggestionOptions(distinctValues(customer => customer?.bank_name)),
    branches: buildSuggestionOptions(distinctValues(customer => customer?.branch_name)),
    customerTypes: buildCustomerTypeOptions(
      distinctValues(customer => customer?.customer_type)
    ),
  }
}

export const getCustomerOrganizationText = customerInfo => {
  const customerTypeKey = resolveCustomerTypeKey(customerInfo?.customer_type)

  return customerTypeKey === "banker"
    ? customerInfo?.bank_name || ""
    : customerInfo?.company_name || ""
}

export const getCustomerDepartmentText = customerInfo => {
  const customerTypeKey = resolveCustomerTypeKey(customerInfo?.customer_type)

  return customerTypeKey === "banker" ? customerInfo?.branch_name || "" : ""
}
